import React, { useEffect, useState } from 'react';
import {
  UserPlus, Trash2, RefreshCw, ShieldCheck,
  AlertCircle, CheckCircle2, KeyRound, X,
} from 'lucide-react';
import { api } from './api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

// Formulaire inline de changement de mot de passe
function PwdForm({ adminId, isSelf, onClose, onSuccess }) {
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) { setErr('8 caractères minimum.'); return; }
    setSaving(true);
    setErr('');
    try {
      await api(`/admins/${adminId}/password`, {
        method: 'PATCH',
        body: { new_password: newPwd, ...(isSelf ? { old_password: oldPwd } : {}) },
      });
      onSuccess();
    } catch (e) {
      setErr(e.message || 'Erreur.');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="pwd-form"
      className="mt-2 mb-1 mx-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 space-y-2.5">
      {isSelf && (
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 mb-1">Ancien mot de passe</label>
          <input
            type="password" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)}
            placeholder="Votre mot de passe actuel"
            required data-testid="old-pwd-input"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-slate-400 bg-white"
          />
        </div>
      )}
      <div>
        <label className="block text-[11px] font-semibold text-slate-500 mb-1">Nouveau mot de passe</label>
        <input
          type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
          placeholder="Minimum 8 caractères"
          required minLength={8} data-testid="new-pwd-input"
          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-slate-400 bg-white"
        />
      </div>
      <div className="flex items-center justify-between gap-3 pt-0.5">
        <span className="text-xs text-red-500 font-medium min-h-[16px]">{err}</span>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="text-xs text-slate-500 hover:text-slate-800 border border-slate-200 bg-white rounded-lg px-3 py-1.5">
            Annuler
          </button>
          <button type="submit"
            disabled={saving || newPwd.length < 8 || (isSelf && !oldPwd)}
            data-testid="pwd-save-btn"
            className="flex items-center gap-1.5 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg px-3 py-1.5 transition-colors">
            <KeyRound size={12} />
            {saving ? 'Enregistrement…' : 'Valider'}
          </button>
        </div>
      </div>
    </form>
  );
}

export default function AdminsTab({ currentAdmin }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  // Formulaire de création
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [creating, setCreating] = useState(false);

  // Feedback global (création, suppression, mdp)
  const [feedback, setFeedback] = useState(null);

  // Quel admin a le panneau "supprimer" ouvert
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Quel admin a le formulaire mdp ouvert
  const [pwdPanel, setPwdPanel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api('/admins');
      setAdmins(data.admins);
    } catch { /* noop */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const flash = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 4000);
  };

  const createAdmin = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !password.trim()) return;
    setCreating(true);
    try {
      await api('/admins', { method: 'POST', body: { name: name.trim(), email: email.trim(), password } });
      setName(''); setEmail(''); setPassword('');
      flash('ok', 'Admin créé avec succès.');
      load();
    } catch (err) {
      flash('err', err.message || 'Erreur lors de la création.');
    } finally { setCreating(false); }
  };

  const deleteAdmin = async (id) => {
    try {
      await api(`/admins/${id}`, { method: 'DELETE' });
      setAdmins((prev) => prev.filter((a) => a.id !== id));
      flash('ok', 'Admin supprimé.');
    } catch (err) {
      flash('err', err.message || 'Erreur lors de la suppression.');
    } finally { setDeleteConfirm(null); }
  };

  return (
    <div data-testid="papa-admins-tab" className="space-y-5">

      {/* ── Liste des admins ── */}
      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-slate-400" />
            <span className="text-sm font-semibold text-slate-700">Administrateurs du panel</span>
            <span className="bg-slate-100 text-slate-500 text-[11px] font-bold rounded-full px-2 py-0.5">{admins.length}</span>
          </div>
          <button onClick={load} data-testid="admins-refresh"
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white hover:bg-slate-50">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
        </div>

        {loading ? (
          <div className="py-10 flex justify-center">
            <RefreshCw size={18} className="animate-spin text-slate-300" />
          </div>
        ) : admins.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">Aucun admin.</p>
        ) : (
          <div className="divide-y divide-slate-50">
            {admins.map((a) => {
              const isSelf = a.id === currentAdmin?.id;
              const pwdOpen = pwdPanel === a.id;
              return (
                <div key={a.id} data-testid="admin-row">
                  {/* Ligne principale */}
                  <div className="flex items-center justify-between gap-3 px-5 py-3.5">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{a.name || '—'}</p>
                      <p className="text-xs text-slate-400 truncate">{a.email}</p>
                      <p className="text-[11px] text-slate-300 mt-0.5">Créé le {fmtDate(a.created_at)}</p>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isSelf && (
                        <span className="text-[10px] font-bold bg-sky-100 text-sky-600 px-2 py-0.5 rounded-full">Vous</span>
                      )}

                      {/* Bouton mot de passe */}
                      <button
                        onClick={() => { setPwdPanel(pwdOpen ? null : a.id); setDeleteConfirm(null); }}
                        data-testid="admin-pwd-btn"
                        title="Changer le mot de passe"
                        className={`p-1.5 rounded-lg transition-colors ${pwdOpen ? 'bg-amber-100 text-amber-600' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50'}`}
                      >
                        <KeyRound size={14} />
                      </button>

                      {/* Bouton supprimer */}
                      {deleteConfirm === a.id ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-500">Confirmer ?</span>
                          <button onClick={() => deleteAdmin(a.id)} data-testid="admin-delete-confirm"
                            className="text-xs font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg px-2.5 py-1 transition-colors">
                            Oui
                          </button>
                          <button onClick={() => setDeleteConfirm(null)}
                            className="text-xs font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-2.5 py-1">
                            Non
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => !isSelf && setDeleteConfirm(a.id)}
                          disabled={isSelf}
                          data-testid="admin-delete-btn"
                          title={isSelf ? 'Impossible de se supprimer soi-même' : 'Supprimer'}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Formulaire mdp inline */}
                  {pwdOpen && (
                    <div className="px-4 pb-3">
                      <PwdForm
                        adminId={a.id}
                        isSelf={isSelf}
                        onClose={() => setPwdPanel(null)}
                        onSuccess={() => {
                          setPwdPanel(null);
                          flash('ok', `Mot de passe de ${a.name || a.email} modifié.`);
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Feedback global ── */}
      {feedback && (
        <div className={`flex items-center gap-2 text-sm font-semibold px-4 py-3 rounded-xl border ${
          feedback.type === 'ok'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-red-50 text-red-700 border-red-200'
        }`}>
          {feedback.type === 'ok'
            ? <CheckCircle2 size={15} />
            : <AlertCircle size={15} />}
          {feedback.msg}
        </div>
      )}

      {/* ── Formulaire de création ── */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus size={16} className="text-slate-400" />
          <span className="text-sm font-semibold text-slate-700">Créer un nouvel admin</span>
        </div>

        <form onSubmit={createAdmin} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Nom</label>
              <input
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Prénom Nom" required
                data-testid="admin-name-input"
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-slate-400 bg-slate-50"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">E-mail</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@exemple.com" required
                data-testid="admin-email-input"
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-slate-400 bg-slate-50"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Mot de passe</label>
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 caractères"
              required minLength={8}
              data-testid="admin-password-input"
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-slate-400 bg-slate-50"
            />
          </div>
          <div className="flex items-center justify-end pt-1">
            <button
              type="submit"
              disabled={creating || !name.trim() || !email.trim() || password.length < 8}
              data-testid="admin-create-btn"
              className="flex items-center gap-2 bg-slate-900 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
            >
              <UserPlus size={14} />
              {creating ? 'Création…' : "Créer l'admin"}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
}
